import { SparklesIcon } from '@heroicons/react/24/outline';
import {
  BotMessageSquare,
  FileText,
  Heart,
  Instagram,
  Linkedin,
  Mic,
  Monitor,
  MoreHorizontal,
  Newspaper,
  Package,
  RefreshCw,
  Search,
  Ticket,
  Twitter,
  User,
  Users,
  Wrench,
  Youtube,
} from 'lucide-react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Formik } from 'formik';
import { stringify } from 'query-string';
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import * as Yup from 'yup';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import useUserQueries from '@dashboard/hooks/queries/useUserQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import useBrandFetch from '@dashboard/hooks/useBrandFetch';
import StripePartner from '@dashboard/icons/StripePartnerIcon';
import { tracking, useTracking } from '@dashboard/lib/tracking';
import { rootDomain } from '@dashboard/services/env';
import getStripeConnectUrl from '@dashboard/services/getStripeConnectUrl';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import { isGenericEmailDomain, getWebsiteFromEmail } from '@dashboard/utils/emailDomain';
import { generateSubdomain } from '@dashboard/utils/generateSubdomain';
import { fetcher } from '@dashboard/services/request';
import { setupPageCountKey } from '@dashboard/lib/queryKeys';

import { TextInput as FormikTextInput } from '@dashboard/components/Formik';
import Form from '@dashboard/components/Formik/Form';
import { Label } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import Logo from '@dashboard/components/Logo';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import { Input } from '@dashboard/components/ui/input';
import { StepDots } from '@dashboard/components/ui/step-dots';
import StepTransition from '@dashboard/components/StepTransition';
import RadioCard from '@dashboard/components/Formik/components/RadioCard';
import { cn } from '@dashboard/lib/utils';
import { getStoreSetupNext, navigateFromDashboard } from '@dashboard/utils/storeSetupRedirect';

const INDUSTRY_OPTIONS = [
  { label: 'Event tickets', value: 'event-tickets', icon: Ticket },
  { label: 'Digital products', value: 'digital-products', icon: FileText },
  { label: 'Physical products', value: 'physical-products', icon: Package },
  { label: 'Memberships', value: 'memberships', icon: Users },
  { label: 'Subscriptions', value: 'subscriptions', icon: RefreshCw },
  { label: 'Software & SaaS', value: 'software-saas', icon: Monitor },
  { label: 'Services', value: 'services', icon: Wrench },
  { label: 'Donations', value: 'donations', icon: Heart },
];

const REFERRAL_OPTIONS = [
  { label: 'Google search', value: 'google', icon: Search },
  { label: 'From a friend', value: 'friend', icon: User },
  { label: 'Blog article', value: 'blog', icon: Newspaper },
  { label: 'AI chat', value: 'ai-chat', icon: BotMessageSquare },
  { label: 'Podcast', value: 'podcast', icon: Mic },
  { label: 'YouTube', value: 'youtube', icon: Youtube },
  { label: 'LinkedIn', value: 'linkedin', icon: Linkedin },
  { label: 'Instagram', value: 'instagram', icon: Instagram },
  { label: 'X / Twitter', value: 'twitter', icon: Twitter },
  { label: 'Other', value: 'other', icon: MoreHorizontal },
];

const brandLoadingMessages = [
  'Fetching your website...',
  'Looking for your branding...',
  'Saving your logo...',
  'Detecting brand colors...',
  'Checking your store name...',
  'Almost there...',
];

function BrandLoadingState() {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(i => {
        if (i >= brandLoadingMessages.length - 1) return i;
        setIsVisible(false);
        setTimeout(() => {
          setMessageIndex(prev => prev + 1);
          setIsVisible(true);
        }, 200);
        return i;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center text-center">
      <div className="relative mb-6">
        <SparklesIcon className="w-10 h-10 text-foreground animate-float" />
      </div>
      <h1 className="text-xl mb-2 text-balance">Setting up your brand</h1>
      <p
        className="text-sm text-gray-400 dark:text-muted-foreground h-6 transition-all duration-200 ease-out"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(4px)',
        }}
      >
        {brandLoadingMessages[messageIndex]}
      </p>
    </div>
  );
}

const SellerStart = () => {
  useTracking();
  const { seller, user, token, isAuthenticating, refreshAvailableStores, updateAuthContext } =
    useAuth();

  const history = useHistory();
  const location = useLocation();
  const urlQuery = useUrlQuery();

  const isDashboardCreatingStore =
    new URLSearchParams(location.search).get('dashboard_create_store') === 'true';

  // Prefetch brand cache early so BrandStep has data ready and doesn't flash
  // Skip prefetch when creating a new store — brand data will come from the website step
  useBrandFetch({ autoStart: !isDashboardCreatingStore });

  const sellerId = seller?._id;
  const { data: pageCount } = useQuery({
    queryKey: setupPageCountKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}/pages/count`, token),
    enabled: !!sellerId && !!token,
    staleTime: 5 * 60 * 1000,
  });

  const hasPages = pageCount > 0;
  const onboardingDestination = hasPages ? '/pages' : '/pages/new?onboarding=1';
  const storeSetupNext = getStoreSetupNext(location.search);
  const setupDestination =
    storeSetupNext || (isDashboardCreatingStore ? '/' : onboardingDestination);

  const isGenericEmail = useMemo(() => isGenericEmailDomain(user?.email), [user?.email]);

  const stepDefinitions = useMemo(
    () => [
      ...(isGenericEmail || isDashboardCreatingStore ? [{ id: 'website' }] : []),
      ...(!isDashboardCreatingStore ? [{ id: 'name' }] : []),
      { id: 'industry' },
      ...(!isDashboardCreatingStore ? [{ id: 'referral' }] : []),
      { id: 'brand' },
      { id: 'stripe' },
    ],
    [isGenericEmail, isDashboardCreatingStore],
  );

  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    const queryStep = parseInt(new URLSearchParams(location.search).get('step'));
    if (queryStep && queryStep >= 1 && queryStep <= stepDefinitions.length) {
      return queryStep - 1;
    }
    return 0;
  });

  const currentStep = stepDefinitions[currentStepIndex] || stepDefinitions[0];

  useEffect(() => {
    tracking.trackPageview();
  }, [location]);

  // Sync URL with current step
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlStep = parseInt(queryParams.get('step'));
    if (urlStep !== currentStepIndex + 1) {
      history.replace({
        search: stringify({
          ...urlQuery,
          step: currentStepIndex + 1,
        }),
      });
    }
  }, [currentStepIndex]);

  // If on stripe step and already connected, redirect to dashboard
  useEffect(() => {
    if (currentStep?.id === 'stripe' && checkIsConnectedToStripe(seller)) {
      navigateFromDashboard(setupDestination, history.replace);
    }
  }, [currentStep?.id, seller?._id, setupDestination]);

  function goToNextStep() {
    if (currentStepIndex < stepDefinitions.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  }

  const [isExiting, setIsExiting] = useState(false);

  function goToDashboard() {
    setIsExiting(true);
    refreshAvailableStores().then(() => {
      updateAuthContext({ user, seller });
      navigateFromDashboard(setupDestination, history.replace);
    });
  }

  return (
    <div
      className={`min-h-screen relative bg-white dark:bg-background transition-opacity duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div className="absolute top-0 left-0 p-2">
        <div className="flex h-10 items-center pl-2">
          <Logo size={20} />
        </div>
      </div>

      <div className="flex flex-col min-h-screen items-center pt-[15vh] px-4">
        {!isAuthenticating && (
          <div className="w-full max-w-2xl">
            <StepTransition stepKey={currentStep.id}>
              {currentStep.id === 'website' && <WebsiteStep onNext={goToNextStep} />}
              {currentStep.id === 'name' && <NameStep onNext={goToNextStep} />}
              {currentStep.id === 'industry' && <IndustryStep onNext={goToNextStep} />}
              {currentStep.id === 'referral' && <ReferralStep onNext={goToNextStep} />}
              {currentStep.id === 'brand' && (
                <BrandStep
                  onNext={goToNextStep}
                  isDashboardCreatingStore={isDashboardCreatingStore}
                />
              )}
              {currentStep.id === 'stripe' && (
                <ConnectStripe
                  onNext={goToDashboard}
                  hasPages={hasPages}
                  nextPath={storeSetupNext}
                />
              )}
            </StepTransition>
          </div>
        )}

        <div className="fixed bottom-8">
          <StepDots
            total={
              stepDefinitions.length +
              (hasPages || isDashboardCreatingStore || storeSetupNext ? 0 : 3)
            }
            current={currentStepIndex}
            onNavigate={setCurrentStepIndex}
            maxClickable={currentStepIndex}
          />
        </div>
      </div>
    </div>
  );
};

function WebsiteStep({ onNext }) {
  const { seller, mergeSeller } = useAuth();
  const { updateSeller } = useSellerQueries();
  const { fetchBrand, isLoading, isPolling } = useBrandFetch({ autoStart: false });
  const [url, setUrl] = useState(() => (seller.url || '').replace(/^https?:\/\//, ''));

  function handleChange(e) {
    setUrl(e.target.value.replace(/^https?:\/\//, ''));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = url.trim();
    if (trimmed) {
      const fullUrl = `https://${trimmed}`;
      mergeSeller({ url: fullUrl });
      updateSeller({ url: fullUrl });
      if (!isLoading && !isPolling) {
        fetchBrand(fullUrl);
      }
    }
    onNext();
  }

  return (
    <div className="text-center">
      <h1 className="text-xl mb-8 text-balance">What's your website?</h1>

      <form onSubmit={handleSubmit} className="max-w-md mx-auto">
        <div className="flex w-full rounded-lg border border-input overflow-hidden transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50">
          <div className="bg-gray-50 dark:bg-card flex items-center px-3 text-gray-500 dark:text-muted-foreground">
            https://
          </div>
          <Input
            type="text"
            name="website"
            placeholder="mybusiness.com"
            value={url}
            onChange={handleChange}
            autoFocus
            className="border-0 rounded-none focus-visible:ring-0 focus-visible:border-transparent"
          />
        </div>
        <ShadcnButton type="submit" className="w-full mt-4" size="xl">
          Continue
        </ShadcnButton>
      </form>
    </div>
  );
}

function NameStep({ onNext }) {
  const { user, setUser } = useAuth();
  const { updateUser } = useUserQueries();
  const [name, setName] = useState(user.name || '');
  const [error, setError] = useState('');
  const [showGreeting, setShowGreeting] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    setError('');
    updateUser({ name: name.trim() }, user._id);
    setUser({ ...user, name: name.trim() });

    setShowGreeting(true);
    setTimeout(() => {
      onNext();
    }, 1500);
  }

  if (showGreeting) {
    return (
      <div className="text-center animate-in fade-in zoom-in duration-300">
        <h1 className="text-xl text-balance">Nice to meet you, {name.trim().split(' ')[0]} 👋</h1>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl mb-8 text-center text-balance">What should we call you?</h1>

      <form onSubmit={handleSubmit} className="max-w-md mx-auto">
        <Label htmlFor="onboarding-name">Your name</Label>
        <Input
          id="onboarding-name"
          name="name"
          type="text"
          autoComplete="name"
          placeholder="Your name"
          value={name}
          onChange={e => {
            setName(e.target.value);
            if (error) setError('');
          }}
          autoFocus
        />
        {error && <p className="text-red-500 text-sm mt-2 text-left">{error}</p>}
        <ShadcnButton type="submit" className="w-full mt-4" size="xl">
          Continue
        </ShadcnButton>
      </form>
    </div>
  );
}

function RadioCardGrid({ name, options, value, onSelect, gridClassName }) {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    const timers = options.map((_, i) =>
      setTimeout(() => setVisibleCount(prev => prev + 1), 60 * (i + 1)),
    );
    return () => timers.forEach(clearTimeout);
  }, [options.length]);

  function handleChange(selected) {
    setTimeout(() => onSelect(selected), 300);
  }

  return (
    <Formik initialValues={{ [name]: value || '' }} onSubmit={() => {}}>
      {() => (
        <RadioCard.Group name={name} className={gridClassName}>
          {options.map((option, index) => {
            const Icon = option.icon;
            const isVisible = index < visibleCount;
            return (
              <RadioCard.Card
                key={option.value}
                name={name}
                value={option.value}
                bare
                onChange={handleChange}
                className={cn(
                  'flex flex-col items-center justify-center gap-2 px-2 py-4 min-h-[96px] text-center',
                  'transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]',
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
                )}
              >
                <Icon
                  className="h-5 w-5 text-gray-700 dark:text-muted-foreground"
                  strokeWidth={1.75}
                />
                <span className="text-xs leading-tight text-balance">{option.label}</span>
              </RadioCard.Card>
            );
          })}
        </RadioCard.Group>
      )}
    </Formik>
  );
}

function IndustryStep({ onNext }) {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();

  function handleSelect(value) {
    updateSeller({ industry: value });
    tracking.setUser({ industry: value });
    onNext();
  }

  return (
    <div className="text-center">
      <h1 className="text-xl mb-8 text-balance">What do you sell?</h1>
      <RadioCardGrid
        name="industry"
        options={INDUSTRY_OPTIONS}
        value={seller?.industry}
        onSelect={handleSelect}
        gridClassName="gap-3 grid-cols-3 sm:grid-cols-4"
      />
    </div>
  );
}

function ReferralStep({ onNext }) {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();

  function handleSelect(value) {
    updateSeller({ referralSource: value });
    tracking.setUser({ referralSource: value });
    onNext();
  }

  return (
    <div className="text-center">
      <h1 className="text-xl mb-8 text-balance">How did you hear about us?</h1>
      <RadioCardGrid
        name="referralSource"
        options={REFERRAL_OPTIONS}
        value={seller?.referralSource}
        onSelect={handleSelect}
        gridClassName="gap-3 grid-cols-4 sm:grid-cols-5"
      />
    </div>
  );
}

function BrandStep({ onNext, isDashboardCreatingStore }) {
  const { seller, mergeSeller, user } = useAuth();
  const { updateSeller } = useSellerQueries();

  const { hasCustomDomain, detectedWebsite } = useMemo(() => {
    const email = user?.email;
    if (!email) {
      return { hasCustomDomain: false, detectedWebsite: null };
    }
    const isGeneric = isGenericEmailDomain(email);
    const website = getWebsiteFromEmail(email);
    return {
      hasCustomDomain: !isGeneric && !!website,
      detectedWebsite: website,
    };
  }, [user?.email]);

  // When creating a new store, only use the URL entered in the website step (seller.url),
  // don't fall back to the email domain which would pull the previous store's branding
  const fallbackUrl = isDashboardCreatingStore
    ? seller.url || null
    : seller.url || (hasCustomDomain ? detectedWebsite : null);
  const { brandData, isLoading, isPolling, isCompleted, isIdle, isFetchedAfterMount } =
    useBrandFetch({
      autoStart: true,
      fallbackUrl,
    });

  const [subdomainAutoGenerated, setSubdomainAutoGenerated] = useState(false);
  const touchedFieldsRef = useRef({});

  const markFieldTouched = useCallback(fieldName => {
    touchedFieldsRef.current[fieldName] = true;
  }, []);

  // The store URL is saved only when this step is submitted, so a seller that has one
  // chose its logo and color here. Before that, the color is only the schema default.
  const hasSavedBranding = !!seller.name;

  // The brand color saves to the seller while the store name and URL are still unsaved,
  // so this only seeds the form: it must not reinitialize from the seller.
  const initialValues = useMemo(() => {
    const displayName = seller.displayName || brandData?.name || '';
    // The API's subdomain is checked for availability; the local slug is not
    const suggestedName =
      (displayName === brandData?.name && brandData?.subdomain) || generateSubdomain(displayName);
    const brandLogo = brandData?.logo?.file ? brandData.logo : null;

    return {
      displayName,
      name: seller.name || suggestedName,
      logo: (hasSavedBranding ? null : brandLogo) || seller.logo || null,
      brandColor: (hasSavedBranding ? null : brandData?.brandColor) || seller.brandColor || '',
    };
  }, [
    brandData,
    hasSavedBranding,
    seller.displayName,
    seller.name,
    seller.logo,
    seller.brandColor,
  ]);

  useEffect(() => {
    if (seller.name) {
      setSubdomainAutoGenerated(false);
      return;
    }
    if (brandData?.name && !seller.name && !touchedFieldsRef.current.name) {
      setSubdomainAutoGenerated(true);
    }
  }, [brandData, seller.name]);

  const hasAppliedBrandDataRef = useRef(false);
  useEffect(() => {
    if (!brandData || hasSavedBranding || hasAppliedBrandDataRef.current) return;

    const updates = {};
    if (brandData.brandColor) updates.brandColor = brandData.brandColor;
    if (brandData.logo && brandData.logo.file) updates.logo = brandData.logo;

    if (Object.keys(updates).length > 0) {
      hasAppliedBrandDataRef.current = true;
      mergeSeller(updates);
      updateSeller(updates);
    }
  }, [brandData, hasSavedBranding, mergeSeller, updateSeller]);

  const brandFound =
    isCompleted && brandData && (brandData.name || brandData.logo || brandData.brandColor);
  // The cache read on an earlier step can be older than the brand fetch result
  const stillLoading = isLoading || isPolling || (isIdle && !!fallbackUrl) || !isFetchedAfterMount;

  // Debounce loading state to prevent flash when brand data arrives almost immediately
  // (e.g., when prefetch already cached the result during earlier steps)
  const [showLoading, setShowLoading] = useState(false);
  useEffect(() => {
    if (stillLoading) {
      const timer = setTimeout(() => setShowLoading(true), 300);
      return () => clearTimeout(timer);
    }
    setShowLoading(false);
  }, [stillLoading]);

  const dynamicTitle = brandFound ? 'Review your branding' : 'Add your branding';

  const handleDisplayNameBlur = async (e, setFieldValue, currentSubdomain) => {
    const displayName = e.target.value;

    const currentGeneratedFromDisplay =
      !seller.name && seller.displayName ? generateSubdomain(seller.displayName) : '';

    const wasAutoGenerated =
      subdomainAutoGenerated ||
      !currentSubdomain ||
      currentSubdomain === currentGeneratedFromDisplay;

    if (wasAutoGenerated && displayName && !touchedFieldsRef.current.name) {
      const newSubdomain = generateSubdomain(displayName);
      setFieldValue('name', newSubdomain);
      setSubdomainAutoGenerated(true);
    }
  };

  if (showLoading) {
    return <BrandLoadingState />;
  }

  // During debounce period, render nothing instead of flashing the form
  // (StepTransition's fade-in animation masks the empty frame)
  if (stillLoading) {
    return null;
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center">
        <h1 className="text-xl mb-8 text-balance">{dynamicTitle}</h1>
      </div>

      <Form
        initialValues={initialValues}
        showState={false}
        onSubmit={async values => {
          const { data, error } = await updateSeller(values);

          return {
            data,
            error,
            onSuccess: () => {
              if (data?.logo?.url) {
                tracking.setUser({ $avatar: data.logo.url });
              }
              onNext();
            },
          };
        }}
        validationSchema={Yup.object().shape({
          displayName: Yup.string().required('Store name is required'),
          name: Yup.string()
            .required('Store URL is required')
            .min(3)
            .max(50)
            .matches(/^[a-z0-9-]+$/, 'Only lowercase letters, numbers, and hyphens'),
        })}
        fields={[
          {
            name: 'logo',
            required: false,
            type: 'image',
            label: 'Logo',
            image: initialValues.logo,
            crop: { height: 512, width: 512, aspect: 1 },
            recommended: 'Recommended size is 512 x 512px',
            onUpdate: async image => {
              mergeSeller({ logo: image });
              await updateSeller({ logo: image });
            },
            increaseMargin: true,
          },
          {
            name: 'brandColor',
            required: false,
            type: 'color-select',
            label: 'Brand color',
            onChange: async color => {
              mergeSeller({ brandColor: color });
              await updateSeller({ brandColor: color });
            },
            increaseMargin: true,
          },
          {
            name: 'displayName',
            required: true,
            type: 'custom',
            label: 'Store name',
            renderField: (field, { setFieldValue, values }) => (
              <FormikTextInput
                {...field}
                required
                placeholder="e.g. Acme Inc"
                onBlur={e => {
                  field.onBlur?.(e);
                  handleDisplayNameBlur(e, setFieldValue, values.name);
                }}
              />
            ),
            increaseMargin: true,
          },
          {
            name: 'name',
            required: true,
            type: 'custom',
            label: 'Store URL',
            renderField: field => (
              <div className="flex w-full">
                <FormikTextInput
                  {...field}
                  required
                  className="rounded-r-none w-full relative focus-visible:z-10"
                  onBlur={e => {
                    field.onBlur?.(e);
                    markFieldTouched('name');
                  }}
                />
                <div className="bg-muted h-8 w-2/3 flex items-center rounded-r-lg border border-input border-l-0 px-2.5 py-1 text-sm text-foreground">
                  .{rootDomain}
                </div>
              </div>
            ),
            increaseMargin: true,
          },
        ]}
        formSubmit={() => (
          <ShadcnButton type="submit" className="group w-full mt-4" size="xl">
            Continue
          </ShadcnButton>
        )}
      />
    </div>
  );
}

function ConnectStripe({ onNext, hasPages, nextPath }) {
  const defaultReturnPath = hasPages ? '/dashboard/pages' : '/dashboard/pages/new?onboarding=1';
  const stripeReturnPath = nextPath ? `/dashboard${nextPath}` : defaultReturnPath;

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center">
        <h1 className="text-xl mb-8 text-balance">Connect with Stripe</h1>
      </div>

      <div className="flex flex-col gap-3 mb-6">
        {[
          'All funds go directly into your Stripe account',
          'You are free to disconnect at any time',
          'You must connect to accept payments',
        ].map(text => (
          <div key={text} className="flex items-center gap-3">
            <CheckCircleIcon className="h-5 w-5 text-foreground shrink-0" />
            <span className="text-gray-700 dark:text-muted-foreground text-sm">{text}</span>
          </div>
        ))}
      </div>

      <ShadcnButton asChild className="w-full mt-4" size="xl">
        <a href={getStripeConnectUrl(stripeReturnPath)} target="_top">
          Connect with Stripe
        </a>
      </ShadcnButton>

      <div className="mt-4 text-center">
        <button type="button" onClick={onNext}>
          <Link variant="secondary" size="small" className="text-sm">
            Do this later
          </Link>
        </button>
      </div>

      <p className="mt-8 text-center text-xs text-gray-400 dark:text-muted-foreground">
        If you have another platform connected to your Stripe account, you will be asked to create a
        sub-account.{' '}
        <Link to="/docs/articles/connect-with-stripe#create-a-subaccount-on-stripe" target="_blank">
          Learn more
        </Link>
      </p>

      <div className="mt-4 flex justify-center">
        <a href="https://stripe.partners/directory/checkout-page" target="_blank" rel="noreferrer">
          <StripePartner />
        </a>
      </div>
    </div>
  );
}

export default SellerStart;
